/**
 * The markers of `MoversLayer` in a Leaflet layer, kept by id: added, moved in place and removed
 * as the movers change, never drawn again on a tick. Leaflet only, no React, so a test drives it
 * in a DOM.
 *
 * A marker is the Dynamic twin's hexagon. A mover with a heading gets a nose on a wrapper of its
 * own, so a new heading turns the wrapper in place, gliding the short way round (`headingRotation`).
 */
import L from 'leaflet';
import { headingRotation } from '../heading';
import { diffPositions, type LatLngTuple } from '../markerDiff';
import { escapeHtml, markerInnerHtml } from '../markerIcons';
import type { MarkerColors } from './makeMarkerIcon';
import { setMarkerInteractive } from './markerInteraction';

/** A moving twin to mark. Any object with these fields works, and `colorOf` gets it back whole. */
export interface MoverEntry {
  /** The same on every update: the marker is found by it. */
  id: string;
  /** Where it is now. A mover with a coordinate that is not a finite number gets no marker. */
  position: { latitude: number; longitude: number };
  /** Degrees clockwise from north: a nose on the marker points there. Null or left out: no nose. */
  heading?: number | null;
  /** Its name, on the tooltip; null or left out shows the id. */
  label?: string | null;
  /** Its model's name, on the tooltip. */
  modelName?: string | null;
}

/** How every marker shows: its colours, how high it stacks, whether it takes clicks. */
export interface MoverMarkersOptions<M extends MoverEntry> {
  colorOf: (mover: M) => MarkerColors;
  zIndexOffset: number;
  interactive: boolean;
}

// The hexagon, and the box around it that leaves room for the nose.
const SIZE = 16;
const BOX = 26;

// What the layer keeps of each marker: the key of the icon last set (an unchanged icon is not set
// again), the tooltip last set, and the rotation the nose shows (null: no nose).
interface MoverNote {
  marker: L.Marker;
  icon: string;
  tooltip: string;
  rotation: number | null;
}

export class MoverMarkers<M extends MoverEntry> {
  private readonly notes = new Map<string, MoverNote>();
  // The position the layer last gave each marker (`diffPositions`).
  private readonly last = new Map<string, LatLngTuple>();
  private zIndexOffset: number | undefined;
  private interactive = true;
  private readonly layer: L.LayerGroup;
  private readonly onClick: (id: string) => void;

  /** Markers go into `layer`; a click on one calls `onClick` with its id. */
  constructor(layer: L.LayerGroup, onClick: (id: string) => void) {
    this.layer = layer;
    this.onClick = onClick;
  }

  /** Brings the markers in line with `movers`. */
  update(movers: readonly M[], { colorOf, zIndexOffset, interactive }: MoverMarkersOptions<M>) {
    const byId = new Map<string, M>();
    const positions = new Map<string, [number, number]>();
    for (const mover of movers) {
      const { latitude, longitude } = mover.position;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
      byId.set(mover.id, mover);
      positions.set(mover.id, [latitude, longitude]);
    }
    const { added, moved, removed } = diffPositions(this.last, positions);

    for (const id of removed) {
      const note = this.notes.get(id);
      if (note) this.layer.removeLayer(note.marker);
      this.notes.delete(id);
    }
    for (const id of moved) this.notes.get(id)?.marker.setLatLng(positions.get(id)!);
    if (zIndexOffset !== this.zIndexOffset || interactive !== this.interactive) {
      this.zIndexOffset = zIndexOffset;
      this.interactive = interactive;
      for (const { marker } of this.notes.values()) {
        marker.setZIndexOffset(zIndexOffset);
        setMarkerInteractive(marker, interactive);
      }
    }
    for (const id of added) {
      const marker = L.marker(positions.get(id)!, { interactive, zIndexOffset });
      marker.on('click', () => this.onClick(id));
      this.notes.set(id, { marker, icon: '', tooltip: '', rotation: null });
    }
    for (const [id, mover] of byId) {
      const note = this.notes.get(id);
      if (note) this.restyle(note, mover, colorOf(mover));
    }
    for (const id of added) this.layer.addLayer(this.notes.get(id)!.marker);
  }

  private restyle(note: MoverNote, mover: M, colors: MarkerColors) {
    const { marker } = note;
    const heading =
      typeof mover.heading === 'number' && Number.isFinite(mover.heading) ? mover.heading : null;
    const rotation =
      heading === null
        ? null
        : (headingRotation(note.rotation ?? undefined, heading) ?? note.rotation);
    const icon = [colors.fill, colors.stroke, heading !== null].join('|');
    if (icon !== note.icon) {
      note.icon = icon;
      note.rotation = rotation;
      marker.setIcon(moverIcon(mover.id, colors, rotation));
    } else if (rotation !== note.rotation) {
      note.rotation = rotation;
      const nose = marker.getElement()?.querySelector<HTMLElement>('[data-tt-heading]');
      if (nose) nose.style.transform = `rotate(${rotation}deg)`;
      else marker.setIcon(moverIcon(mover.id, colors, rotation));
    }
    const tooltip = moverTooltip(mover);
    if (tooltip !== note.tooltip) {
      note.tooltip = tooltip;
      if (marker.getTooltip()) marker.setTooltipContent(tooltip);
      else marker.bindTooltip(tooltip, { direction: 'top' });
    }
  }
}

/** The marker of a mover: the hexagon, and a nose at `rotation` degrees when not null. */
function moverIcon(id: string, colors: MarkerColors, rotation: number | null): L.DivIcon {
  const shape = markerInnerHtml({
    mode: 'Dynamic',
    size: SIZE,
    fill: colors.fill,
    stroke: colors.stroke,
    isSelected: false,
  });
  // The nose sits on its own wrapper, so a new heading turns it without drawing the icon again.
  const nose =
    rotation === null
      ? ''
      : `<div data-tt-heading style="position:absolute;inset:0;transform:rotate(${rotation}deg);transition:transform 0.4s linear"><div style="position:absolute;left:50%;top:0;margin-left:-4px;border-left:4px solid transparent;border-right:4px solid transparent;border-bottom:5px solid ${colors.stroke}"></div></div>`;
  const inset = (BOX - SIZE) / 2;
  return L.divIcon({
    className: '',
    // data-tt-mover names the mover, for scripts and tests that drive the map.
    html: `<div data-tt-mover="${escapeHtml(id)}" style="position:relative;width:${BOX}px;height:${BOX}px">${nose}<div style="position:absolute;left:${inset}px;top:${inset}px">${shape}</div></div>`,
    iconSize: [BOX, BOX],
    iconAnchor: [BOX / 2, BOX / 2],
    tooltipAnchor: [0, -BOX / 2],
  });
}

function moverTooltip(mover: MoverEntry): string {
  return [
    `<strong>${escapeHtml(mover.label ?? mover.id)}</strong>`,
    mover.label ? `<span style="opacity:0.7">${escapeHtml(mover.id)}</span>` : null,
    mover.modelName ? escapeHtml(mover.modelName) : null,
  ]
    .filter(Boolean)
    .join('<br/>');
}
